import { useState } from 'react'
import { Button, ComboBox, Input, ListBox, ListBoxItem, Popover } from 'react-aria-components'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface StaticOption {
	label: string
	value: string
}

interface Props {
	id?: string
	ariaLabel?: string
	ariaLabelledBy?: string
	ariaInvalid?: boolean
	ariaDescribedBy?: string
	disabled?: boolean
	placeholder?: string
	options: StaticOption[]
	value: string | undefined
	onChange: (next: string | undefined) => void
	onBlur?: () => void
}

/**
 * A ComboBox over a fixed list of options — same visual & keyboard behaviour
 * as AutocompleteField, but the list comes from `field.options` instead of
 * a data source. Used for SELECT and PRIORITY field types so every dropdown
 * in the form feels the same.
 *
 * Designed to slot into `StandardFieldRow` which owns the external label
 * and error UI — this component does NOT render a Label of its own.
 */
export function StaticComboBoxField({
	id,
	ariaLabel,
	ariaLabelledBy,
	ariaInvalid,
	ariaDescribedBy,
	disabled,
	placeholder,
	options,
	value,
	onChange,
	onBlur,
}: Props) {
	const items = options.map((o) => ({ id: o.value, ...o }))
	const initialInput = value ? (options.find((o) => o.value === value)?.label ?? value) : ''
	const [inputValue, setInputValue] = useState(initialInput)
	const handleSelectionChange = buildSelectionChangeHandler(options, onChange, setInputValue)

	return (
		<ComboBox
			id={id}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			defaultItems={items}
			inputValue={inputValue}
			onInputChange={setInputValue}
			onSelectionChange={handleSelectionChange}
			selectedKey={value ?? null}
			isDisabled={disabled}
			allowsEmptyCollection
			menuTrigger="focus"
			className="relative flex flex-col gap-1"
			onBlur={onBlur}
		>
			<Input placeholder={placeholder ?? 'Select…'} className={cn(INPUT_CLASS, 'pr-9')} />
			<Button
				aria-label="Open options"
				className="absolute right-0 bottom-0 flex h-10 items-center px-2 text-muted-foreground"
			>
				<ChevronDown className="h-4 w-4" aria-hidden />
			</Button>
			<OptionsPopover />
		</ComboBox>
	)
}

function buildSelectionChangeHandler(
	options: StaticOption[],
	onChange: (next: string | undefined) => void,
	setInputValue: (next: string) => void,
) {
	return (val: string | number | null) => {
		if (val === null) {
			onChange(undefined)
			setInputValue('')
			return
		}
		const stringVal = String(val)
		onChange(stringVal)
		const picked = options.find((o) => o.value === stringVal)
		setInputValue(picked?.label ?? stringVal)
	}
}

function OptionsPopover() {
	return (
		<Popover
			placement="bottom start"
			offset={4}
			shouldFlip={false}
			className="z-50 max-h-60 w-[var(--trigger-width)] overflow-auto rounded-md border border-input bg-popover text-popover-foreground shadow-md"
		>
			<ListBox className="p-1 outline-none">
				{(item: StaticOption & { id: string }) => (
					<ListBoxItem
						textValue={item.label}
						className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none data-[focused]:bg-accent data-[focused]:text-accent-foreground hover:bg-accent hover:text-accent-foreground"
					>
						{item.label}
					</ListBoxItem>
				)}
			</ListBox>
		</Popover>
	)
}

const INPUT_CLASS = cn(
	'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background',
	'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
	'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
)
